import { memo, useCallback, useMemo, useRef, useState } from "react";
import Tag from "../../Tag";
import Input from "..";
import DropDownSelection from "../../DropdownSelection";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { KEY } from "../../../data/variables";
import Icon from "../../Icon";
import { ICON } from "../../Icon/data";

type Multiselect = {
  value: string[] | number[]
  options: { [key: string | number]: string }
  onChange: (val: string[] | number[]) => void
  id: string
}

const Multiselect = memo(({
  value = [], options = {}, onChange, id,
}: Multiselect) => {
  const dropdownRef = useRef()
  const inputRef = useRef()
  const searchRef = useRef()

  const [search, setSearch] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const addValue = useCallback((key: string | number) => {
    const newValue = [...value]
    newValue.push(key)
    onChange(newValue)
    setSearch('')
  }, [value, onChange])

  const onKeyDown = useCallback((e: KeyboardEvent, id: string | number) => {
    if (e.key === KEY.ENTER) {
      addValue(id)
    }
  }, [addValue])

  const filteredOptions = useMemo(() => {
    return Object.keys(options).reduce((arr, key) => {
      if (!value.includes(key)) {
        const label = options[key]
        if (search?.length) {
          if (label.toLowerCase().includes(search.toLowerCase())) {
            const temp = {
              label,
              onClick: () => addValue(key),
              onKeyDown: (e: KeyboardEvent) => onKeyDown(e, key)
            }
            arr.push(temp)
          }
        } else {
          const temp = {
            label,
            onClick: () => addValue(key),
            onKeyDown: (e) => onKeyDown(e, key)
          }
          arr.push(temp)
        }
      }
      return arr
    }, [])
  }, [options, value, addValue, onKeyDown, search])

  const removeTag = useCallback((id: string | number) => {
    const newValue = [...value]
    const idx = newValue.indexOf(id)
    newValue.splice(idx, 1)
    onChange(newValue)
  }, [value, onChange])

  return (
    <div className="Multiselect" ref={inputRef} onClick={() => searchRef?.current?.focus()}>
      <div className="Multiselect__search">
        <div className={`Multiselect__search-tags${value.length ? '' : ' empty'}`}>
          {value.map((v) => {
            return (
              <Tag label={options[v]} id={v} size="s" onClose={(v) => removeTag(v)} />
            )
          })}
        </div>
        <Input
          ref={searchRef}
          type="text"
          value={search}
          onInput={(val) => setSearch(val)}
          onFocus={() => setIsOpen(true)}
        />
        <div className="Multiselect__search-icons">
          {value.length > 0 && (
            <div
              className="Multiselect__search-icons__remove"
              role="button"
              tabIndex={0}
              onClick={() => onChange([])}
            >
              <Icon icon={ICON.CLOSE} size={16} />
            </div>
          )}
          <Icon icon={isOpen ? ICON.CHEVRON_UP : ICON.CHEVRON_DOWN} size={16} />
        </div>
      </div>
      {isOpen && (
        <div ref={dropdownRef} className="Multiselect__dropdown">
          <DropDownSelection options={filteredOptions} boundsRef={inputRef} />
        </div>
      )}
    </div>
  )
})

export default Multiselect
